import React, { FunctionComponent, useState, useEffect, TextareaHTMLAttributes } from 'react';
import { useIsMountedRef } from '../../hooks/useIsMountedRef';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

import styles from './textarea.module.css';

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  wrapperClass?: string;
  value?: string;
  focus?: any;
  onChange?: any;
  onFocus?: any;
  onBlur?: any;
  maxLength: number;
  setTextareaExistsSetState: () => void;
}

export const TextArea: FunctionComponent<TextAreaProps> = (props) => {
  const { onChange, value, maxLength = 250, focus, label, setTextareaExistsSetState, ...rest } = props;
  const textareaId = props.id || props.name;
  const [inputValue, setInputValue] = useState(value || '');
  const [isFocus, setIsFocus] = useState(!!focus);
  const isMountedRef = useIsMountedRef();

  const getDebouncedOnFocus = (e: React.FocusEvent<HTMLTextAreaElement>) =>
    debounce(() => {
      if (isMountedRef.current) {
        setIsFocus(true);
        if (props.onFocus) {
          props.onFocus(e);
        }
      }
    }, 250);

  const getDebouncedOnBlur = (e: React.FocusEvent<HTMLTextAreaElement>) =>
    debounce(() => {
      if (isMountedRef.current) {
        setIsFocus(false);
        if (props.onBlur) {
          props.onBlur(e);
        }
      }
    }, 250);

  const inputClassList = classNames({
    [styles.textarea]: true,
    [styles.focus]: isFocus,
    [styles.active]: value && value,
    [styles.valid]: value && value,
  });

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    setTextareaExistsSetState();
  }, []);

  return (
    <div className={styles.wrapper}>
      <label className={`${styles.label}`} htmlFor={textareaId}>
        {label}
      </label>
      <textarea
        {...rest}
        id={textareaId}
        className={inputClassList}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (onChange) {
            onChange(e);
          }
        }}
        value={inputValue}
        maxLength={maxLength}
        onBlur={(e) => {
          e.persist();
          setIsFocus(false);
          const debounceOnBlur = getDebouncedOnBlur(e);
          debounceOnBlur();
        }}
        onFocus={(e) => {
          e.persist();
          setIsFocus(true);
          const debounceOnFocus = getDebouncedOnFocus(e);
          debounceOnFocus();
        }}
      ></textarea>
    </div>
  );
};
